import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
} from '@theme/utilities';

/**
 * Keeps the existing drawer refs, event hooks, submenus,
 * accordion behavior, and focus helpers.
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #operations = new WeakMap();
  #closing = new WeakSet();
  #openFrames = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);

    for (const frame of this.#openFrames.values()) {
      cancelAnimationFrame(frame);
    }

    this.#openFrames.clear();

    // Invalidate callbacks belonging to the previous connection.
    this.#operations = new WeakMap();
    this.#closing = new WeakSet();
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Explicitly control the details state so the browser does not
   * toggle it again after this handler runs.
   */
  toggle(event) {
    event?.preventDefault();

    if (this.isOpen && !this.#closing.has(this.refs.details)) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Supports open(), open(event), and the existing
   * on:click="header-drawer/open/submenu" hook.
   */
  open(target, event) {
    if (target instanceof Event) {
      event = target;
      target = undefined;
    }

    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (
      details === this.refs.details &&
      this.#closing.has(details)
    ) {
      // A rapid reopen should return to the main menu.
      this.#resetSubmenus();

      this.refs.menuDrawer.classList.remove(
        'menu-drawer--has-submenu-opened'
      );
    }

    const operation = this.#beginOperation(details);

    this.#closing.delete(details);

    details.open = true;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    const frame = requestAnimationFrame(() => {
      this.#openFrames.delete(details);

      if (
        !this.#isCurrent(details, operation) ||
        !details.open
      ) {
        return;
      }

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => {
          if (
            this.#isCurrent(details, operation) &&
            this.#isVisibleBranch(details)
          ) {
            trapFocus(details);
          }
        },
        { subtree: false }
      );
    });

    this.#openFrames.set(details, frame);
  }

  back(event) {
    event?.preventDefault();
    this.#close(this.#getDetailsElement(event));
  }

  close(event) {
    event?.preventDefault();
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const operation = this.#beginOperation(details);

    this.#closing.add(details);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        // Ignore this closing callback if another tap has
        // already reopened the same menu.
        if (
          !this.#isCurrent(details, operation) ||
          !this.#closing.has(details)
        ) {
          return;
        }

        reset(details);
        this.#closing.delete(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          this.#resetSubmenus();
        } else if (
          this.#isVisibleBranch(this.refs.details)
        ) {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Cancel any pending opening frame and invalidate older callbacks.
   */
  #beginOperation(details) {
    const frame = this.#openFrames.get(details);

    if (frame !== undefined) {
      cancelAnimationFrame(frame);
      this.#openFrames.delete(details);
    }

    const operation = {};

    this.#operations.set(details, operation);

    return operation;
  }

  #isCurrent(details, operation) {
    return (
      this.isConnected &&
      this.#operations.get(details) === operation
    );
  }

  /**
   * Prevent focus from being trapped inside a closed submenu
   * or a submenu whose parent is closing.
   */
  #isVisibleBranch(details) {
    let current = details;

    while (current && this.contains(current)) {
      if (
        !current.open ||
        this.#closing.has(current) ||
        !current.classList.contains('menu-open')
      ) {
        return false;
      }

      if (current === this.refs.details) {
        return true;
      }

      current = current.parentElement?.closest('details');
    }

    return false;
  }

  #resetSubmenus() {
    const openDetails = this.refs.details.querySelectorAll(
      'details[open]:not(accordion-custom > details)'
    );

    openDetails.forEach((details) => {
      this.#beginOperation(details);
      this.#closing.delete(details);
      reset(details);
    });
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}